import { X } from "lucide-react";
import { AvatarBubble } from "@/components/chatChrome";
import { mentionLabel } from "@/lib/chatMentions";
import { normalizeChatPresence, type ChatColleague } from "@/lib/chatApi";

type Props = {
  candidates: ChatColleague[];
  highlightIndex: number;
  presence: Record<number, string>;
  onPick: (member: ChatColleague) => void;
  onHighlight: (index: number) => void;
  onClose: () => void;
};

export function ChatMentionPicker({
  candidates,
  highlightIndex,
  presence,
  onPick,
  onHighlight,
  onClose,
}: Props) {
  if (candidates.length === 0) return null;
  return (
    <div
      className="chat-mention-drawer"
      role="listbox"
      aria-label="Mentionner un membre"
    >
      <div className="chat-mention-drawer__handle" aria-hidden />
      <header className="flex shrink-0 items-center justify-between border-b border-[var(--chat-border)] px-3 py-2">
        <h3 className="text-sm font-semibold">Mentionner</h3>
        <button type="button" onClick={onClose} aria-label="Fermer">
          <X className="size-4" />
        </button>
      </header>
      <ul className="min-h-0 flex-1 overflow-y-auto py-1">
        {candidates.map((m, i) => {
          const name = mentionLabel(m);
          const active = i === highlightIndex;
          return (
            <li key={m.id}>
              <button
                type="button"
                role="option"
                aria-selected={active}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left ${
                  active ? "bg-[var(--chat-accent-soft)]" : "hover:bg-[var(--chat-surface-elevated)]"
                }`}
                onMouseEnter={() => onHighlight(i)}
                onPointerDown={(e) => {
                  e.preventDefault();
                  onPick(m);
                }}
              >
                <AvatarBubble
                  label={name}
                  url={m.avatar_url}
                  presence={normalizeChatPresence(presence[m.id])}
                  size="sm"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{name}</span>
                  {m.username && m.username !== name ? (
                    <span className="block truncate text-[11px] text-[var(--chat-muted)]">
                      @{m.username}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
